import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { User, Layers, TrendingUp, X } from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface PublisherPromoModalProps {
  session: any;
  isEnabled?: boolean;
}

export const PublisherPromoModal = ({ session, isEnabled = true }: PublisherPromoModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (session || !isEnabled) return;

    const hasSeenPromo = localStorage.getItem("feevho_publisher_promo_seen");
    
    if (!hasSeenPromo) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, [session, isEnabled]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("feevho_publisher_promo_seen", "true");
  };

  const steps = [
    {
      icon: User,
      title: "הרשמה מהירה בחינם",
      desc: "יצירת חשבון מאובטח תוך שניות, ללא כל עלות או התחייבות."
    },
    {
      icon: Layers,
      title: "פרסום המערכת שלך",
      desc: "הוספת תיאור, תמונות וקישורים למוצר הדיגיטלי שבנית."
    },
    {
      icon: TrendingUp,
      title: "חשיפה וצמיחה",
      desc: "קבלת במה מרכזית, דירוגים אותנטיים וטראפיק איכותי למוצר."
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) handleClose();
    }}>
      <DialogContent 
        className="w-[92vw] sm:max-w-[460px] p-6 sm:p-10 border border-slate-100/50 rounded-[2rem] sm:rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] bg-white/95 backdrop-blur-2xl outline-none [&>button]:hidden gap-0" 
        dir="rtl"
      >
        <VisuallyHidden>
          <DialogTitle>הבמה המרכזית למוצר הדיגיטלי שלך.</DialogTitle>
          <DialogDescription>פופ-אפ המציע למשתמשים לפרסם את המערכת שלהם בחינם בקהילת היוצרים של Feevho.</DialogDescription>
        </VisuallyHidden>

        {/* Custom Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f5f7] text-[#86868b] hover:bg-[#e8e8ed] hover:text-[#1d1d1f] transition-colors focus:outline-none"
          aria-label="סגור"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>

        {/* Header Graphic & Text */}
        <div className="text-center mb-8 sm:mb-10 mt-2 sm:mt-0">
          <div className="inline-flex items-center justify-center mx-auto mb-4 sm:mb-5">
            <span className="text-2xl sm:text-3xl font-black text-[#1d1d1f] tracking-tight" dir="ltr">Feevho</span>
          </div>
          <h2 className="text-2xl sm:text-[28px] font-bold text-[#1d1d1f] tracking-tight mb-3 sm:mb-4 leading-tight">
            הבמה המרכזית<br/>למוצר הדיגיטלי שלך.
          </h2>
          <p className="text-[14px] sm:text-[15px] text-[#86868b] leading-relaxed max-w-sm mx-auto font-medium px-2 sm:px-0">
            הצטרפו לקהילת היוצרים של {APP_NAME}. פרסמו את האפליקציה או המערכת שבניתם בחינם, וקבלו חשיפה.
          </p>
        </div>

        {/* Features List */}
        <div className="space-y-5 sm:space-y-6 mb-8 sm:mb-10">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#f5f5f7] flex items-center justify-center shrink-0 border border-white shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                <step.icon className="w-5 h-5 text-[#1d1d1f]" strokeWidth={1.5} />
              </div>
              <div className="pt-0.5 sm:pt-1 text-right">
                <h4 className="font-bold text-[#1d1d1f] mb-0.5 sm:mb-1 text-[15px] sm:text-base tracking-tight">{step.title}</h4>
                <p className="text-[13px] sm:text-sm text-[#86868b] leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-auto">
          <Button 
            asChild 
            className="w-full h-12 sm:h-14 rounded-full bg-[#1d1d1f] hover:bg-black text-white font-bold text-[15px] sm:text-base shadow-lg shadow-slate-200/50 transition-transform active:scale-[0.98]"
          >
            <Link to="/register" onClick={handleClose}>
              להרשמה ופרסום (בחינם)
            </Link>
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleClose} 
            className="w-full h-10 sm:h-12 rounded-full text-[#86868b] hover:text-[#1d1d1f] hover:bg-[#f5f5f7] font-medium text-[13px] sm:text-sm transition-colors"
          >
            אולי בפעם אחרת
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};